import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import { Button, Checkbox, message, Modal, Select, Skeleton, Switch, Tooltip } from "antd"
import { useEffect, useLayoutEffect, useState, useRef, useContext } from "react"
import { Link } from "react-router-dom"
import { ShowMessagePage } from "../../config"
import { getOrders, setOrderShippingStatus, cleanOrphanedResultFiles, fixAllSampleNumbers, updateOrderBillingType } from "../../endpoints"
import { UserContext } from "../../context"
import { isAdmin } from "../../utils/userUtils"

import { DownloadOutlined, EyeOutlined, FileTextOutlined, CheckSquareOutlined, BorderOutlined } from "@ant-design/icons"
import Table, { ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import { useLocation, useNavigate } from "react-router"
import { ContainerFull } from "../../components/GeneralComponents"
import { BillingType, OrderInfoInterface } from "../../types"

const dateFormat = "YYYY-MM-DD"
// value={dayjs('2015-06-06', dateFormat)}

export const AllOrders = (): JSX.Element => {
    const user = useContext(UserContext)
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [shippingLoading, setShippingLoading] = useState<Set<number>>(new Set())
    const [showCanceledOrders, setShowCanceledOrders] = useState<boolean>(false)
    const [showSamplesByDay, setShowSamplesByDay] = useState<boolean>(false)
    const [cleaningFiles, setCleaningFiles] = useState<boolean>(false)
    const [page, setPage] = useState<number>(1)
    const [pageSize, setPageSize] = useState<number>(50)
    const tableRef = useRef<any>(null)
    const navigate = useNavigate()
    const location = useLocation()
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getorders", "all", page, pageSize, showCanceledOrders],
        queryFn: () => getOrders({ type: "all", page, pageSize, showCanceled: showCanceledOrders }),
        refetchOnMount: "always",
    })
    const queryClient = useQueryClient()
    const [messageApi, contextHolder] = message.useMessage()
    const [modal, modalContextHolder] = Modal.useModal()

    // Fix all sample numbers mutation
    const fixAllSampleNumbersMutation = useMutation({
        mutationFn: fixAllSampleNumbers,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                queryClient.invalidateQueries({ queryKey: ["getorders", "all"] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to fix sample numbers")
            console.error("Fix sample numbers error:", error)
        }
    })

    const updateBillingTypeMutation = useMutation({
        mutationFn: updateOrderBillingType,
        onSuccess: (result) => {
            if (result.code === 1000) {
                queryClient.invalidateQueries({ queryKey: ["getorders", "all"] })
            } else {
                messageApi.error(result.message)
            }
        },
        onError: () => {
            messageApi.error("Failed to update billing type")
        },
    })

    useLayoutEffect(() => {
        // console.log("repainting ...")
    }, [])

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "unknown error")
    }

    // Calculate total UnInvoiced samples (server-computed)
    const getUnInvoicedSamplesCount = () => data?.uninvoicedCount || 0

    // Get unique group names for filtering (from current page)
    const getUniqueGroups = () => {
        const groups = new Set<string>()
        let hasUnassigned = false
        
        ;(data?.orders || []).forEach(order => {
            const groupName = order.user?.group_name
            if (groupName) {
                groups.add(groupName)
            } else {
                hasUnassigned = true
            }
        })
        
        const filterOptions = Array.from(groups).sort().map(group => ({
            text: group,
            value: group,
        }))
        
        // Add "No Group" option if there are orders without groups
        if (hasUnassigned) {
            filterOptions.push({
                text: "No Group",
                value: "NO_GROUP",
            })
        }
        
        return filterOptions
    }

    // Compute samples grouped by month > day (from current page)
    const getSamplesByMonth = () => {
        const dayMap: Record<string, { orders: { id: number; ordername: string; nSamples: number }[]; total: number }> = {}
        ;(data?.orders || [])
            .filter(order => order.status !== 'canceled')
            .forEach(order => {
                if (!order.ts) return
                const day = dayjs(order.ts).format(dateFormat)
                if (!dayMap[day]) dayMap[day] = { orders: [], total: 0 }
                const nSamples = order.samples?.length ?? 0
                dayMap[day].orders.push({ id: order.id!, ordername: order.ordername, nSamples })
                dayMap[day].total += nSamples
            })
        const monthMap: Record<string, { children: { key: string; day: string; orders: { id: number; ordername: string; nSamples: number }[]; total: number }[]; total: number }> = {}
        Object.entries(dayMap)
            .sort(([a], [b]) => b.localeCompare(a))
            .forEach(([day, value]) => {
                const month = day.slice(0, 7)
                if (!monthMap[month]) monthMap[month] = { children: [], total: 0 }
                monthMap[month].children.push({ key: day, day, ...value })
                monthMap[month].total += value.total
            })
        return Object.entries(monthMap)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([month, value]) => ({ key: month, day: month, orders: [], ...value }))
    }

    // Handle select all functionality for currently visible rows
    const handleSelectAll = () => {
        const visibleShippedOrderIds = (data?.orders || [])
            .filter(order => order.shipped && order.id !== undefined)
            .map(order => order.id!)
        
        const allVisibleSelected = visibleShippedOrderIds.every(id => selectedRowKeys.includes(id))
        
        if (allVisibleSelected) {
            setSelectedRowKeys(prev => prev.filter(key => !visibleShippedOrderIds.includes(key as number)))
        } else {
            const newSelectedKeys = [...new Set([...selectedRowKeys, ...visibleShippedOrderIds])]
            setSelectedRowKeys(newSelectedKeys)
        }
    }

    // Check if all visible shipped rows are selected
    const getSelectAllState = () => {
        const visibleShippedOrderIds = (data?.orders || [])
            .filter(order => order.shipped && order.id !== undefined)
            .map(order => order.id!)
        
        if (visibleShippedOrderIds.length === 0) return { allSelected: false, someSelected: false }
        
        const selectedVisibleIds = visibleShippedOrderIds.filter(id => selectedRowKeys.includes(id))
        const allSelected = selectedVisibleIds.length === visibleShippedOrderIds.length
        const someSelected = selectedVisibleIds.length > 0
        
        return { allSelected, someSelected }
    }

    const columns: ColumnsType<OrderInfoInterface> = [
        {
            title: "",
            dataIndex: "select",
            align: "center",
            width: 48,
            render: (value, record) =>
                record.id !== undefined ? (
                    <Checkbox
                        checked={selectedRowKeys.includes(record.id)}
                        disabled={!record.shipped}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setSelectedRowKeys([...selectedRowKeys, record.id!])
                            } else {
                                setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id))
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : null,
        },
        {
            title: "Details",
            dataIndex: "id",
            align: "center",
            render: (value, record, index) => {
                return (
                    <Tooltip title="view order">
                        <EyeOutlined
                            style={{ color: "blue" }}
                            onClick={async () => {
                                if (location.pathname.startsWith("/myspace")) {
                                    navigate("/myspace/order/" + record.id)
                                } else if (location.pathname.startsWith("/admin")) {
                                    navigate("/admin/order/" + record.id)
                                } else {
                                    //this should not happen
                                    navigate("/myspace/order/" + record.id)
                                }
                            }}
                        />
                    </Tooltip>
                )
            },
        },
        {
            title: "Results",
            dataIndex: "download",
            align: "center",
            render: (text, record, index) => {
                const allowedStatuses = [
                    "completed",
                    "review-requested",
                    "rerun-completed",
                    "review-completed",
                ]
                const canDownload = allowedStatuses.includes(record.status)
                const url = `/api/download/orderresult/${record.id}?r=${Math.random()}`
                return canDownload ? (
                    <Link to={url} target="_blank" rel="noopener noreferrer">
                        <DownloadOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                    </Link>
                ) : (
                    <DownloadOutlined style={{ fontSize: 18, color: '#bbb', cursor: 'not-allowed', opacity: 0.5 }} />
                )
            },
        },
        
        {
            title: "Order Sheet",
            dataIndex: "orderSheet",
            align: "center",
            render: (text, record, index) => {
                const url = `/api/download/ordersheet/${record.id}?r=${Math.random()}`
                return (
                    <Link to={url} target="_blank" rel="noopener noreferrer">
                        <FileTextOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                    </Link>
                )
            },
        },
        {
            title: "Order ID",
            dataIndex: "id",
            align: "center",
            render: (text, record, index) => {
                const orderPath = location.pathname.startsWith("/myspace")
                    ? `/myspace/order/${record.id}`
                    : `/admin/order/${record.id}`
                return <Link to={orderPath}>{record.id}</Link>
            },
        },
        {
            title: "Order Name",
            dataIndex: "name",
            align: "center",
            render: (text, record, index) => {
                const name = record.ordername
                const displayName = name && name.length > 15 ? name.slice(0, 15) + ".." : name
                const isTruncated = name && name.length > 15
                return isTruncated ? (
                    <Tooltip title={name}>
                        <span style={{ cursor: "help" }}>{displayName}</span>
                    </Tooltip>
                ) : (
                    <span>{displayName}</span>
                )
            },
        },
        {
                title: "Billing Type",
                dataIndex: "billing_type",
                align: "center",
                render: (text, record) => {
                    const billingType = (record.billing_type || "normal") as BillingType
                    return (
                        <Select
                            value={billingType}
                            style={{ width: 140 }}
                            variant="borderless"
                            options={[
                                { value: "normal", label: "normal" },
                                { value: "internal", label: <span style={{ backgroundColor: "#b7eb8f", color: "#135200", padding: "1px 6px", borderRadius: 4 }}>internal</span> },
                                { value: "free rerun", label: <span style={{ backgroundColor: "#ffd591", color: "#873800", padding: "1px 6px", borderRadius: 4 }}>free rerun</span> },
                            ]}
                            loading={updateBillingTypeMutation.isPending}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(value: BillingType) => {
                                if (!record.id) return
                                updateBillingTypeMutation.mutate({
                                    orderId: record.id,
                                    billingType: value,
                                })
                            }}
                        />
                    )
                },
            },
        {
            title: "Status",
            dataIndex: "status",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.status}</span>
            },
        },
        {
            title: "Plates",
            dataIndex: "plateIds",
            align: "center",
            render: (text, record) => {
                // Collect unique plate IDs from all samples' wells
                const plateIdSet = new Set<number>()
                record.samples?.forEach((sample) => {
                    sample.wells?.forEach((well) => {
                        if (well.plateId) plateIdSet.add(well.plateId)
                    })
                })
                const plateIds = Array.from(plateIdSet)
                return plateIds.length > 0 ? (
                    plateIds.map((id, i) => (
                        <span key={id}>
                            <Link to={`/admin/plate/${id}`}>{id}</Link>
                            {i < plateIds.length - 1 && ", "}
                        </span>
                    ))
                ) : (
                    <span style={{ color: "#bbb" }}></span>
                )
            },
        },

        {
            title: "Shipping",
            dataIndex: "id",
            align: "center",
            render: (text, record, index) => {
                const isLoading = shippingLoading.has(record.id!)
                return (
                    <Switch
                        checked={record.shipped}
                        loading={isLoading}
                        style={{ backgroundColor: record.shipped ? "green" : "#ff4d4f" }}
                        checkedChildren={record.shippinginfo?.method === "dropbox" ? "Dropped off" : "Shipped"}
                        unCheckedChildren={<span>Not shipped</span>}
                        onChange={async (value) => {
                            // Set loading state for this specific order
                            setShippingLoading(prev => new Set(prev).add(record.id!))
                            
                            try {
                                const result = await setOrderShippingStatus({
                                    orderId: record.id!,
                                    shipped: value,
                                })
                                
                                if (result.code === 1000) {
                                    // Invalidate getuserorders
                                    queryClient.invalidateQueries({ queryKey: ["getorders", "all"] })
                                }
                            } catch (error) {
                                console.error("Error updating shipping status:", error)
                            } finally {
                                // Remove loading state for this order
                                setShippingLoading(prev => {
                                    const newSet = new Set(prev)
                                    newSet.delete(record.id!)
                                    return newSet
                                })
                            }
                        }}
                    />
                )
            },
        },
                {
            title: "Order Email",
            dataIndex: "orderEmailSent",
            align: "center",
            render: (text, record, index) => {
                return (
                    <span style={{ 
                        color: record.orderEmailSent ? "#52c41a" : "#ff4d4f",
                        fontWeight: "bold" 
                    }}>
                        {record.orderEmailSent ? "✓" : "✗"}
                    </span>
                )
            },
        },
        {
            title: "Result Email",
            dataIndex: "resultEmailSent", 
            align: "center",
            render: (text, record, index) => {
                return (
                    <span style={{ 
                        color: record.resultEmailSent ? "#52c41a" : "#ff4d4f",
                        fontWeight: "bold" 
                    }}>
                        {record.resultEmailSent ? "✓" : "✗"}
                    </span>
                )
            },
        },

        {
            title: "# of Samples",
            dataIndex: "samples",
            align: "center",
            render: (text, record, index) => {
                const sampleNames = record.samples.map((s) => s.sampleName)
                return <span>{JSON.stringify(sampleNames.length)}</span>
            },
        },
        {
            title: "Submission",
            dataIndex: "submission_type",
            align: "center",
            render: (text, record, index) => {
                const submissionType = record.submission_type || 'unknown'
                const displayText = {
                    'unknown': '',
                    'tubes': 'Tubes',
                    'plate-column-order': 'Plate (Col)',
                    'plate-row-order': 'Plate (Row)',
                    'plate-map': 'Plate (Map)'
                }[submissionType] || ''
                return <span>{displayText}</span>
            },
        },
        {
            title: "Service",
            dataIndex: "productname",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.productname}</span>
            },
        },
        {
            title: "Template",
            dataIndex: "samples",
            align: "center",
            render: (text, record, index) => {
                const hasTemplate = record.samples?.some(sample => sample.sequence && sample.sequence.length > 0) ?? false
                return (
                    <span style={{ 
                        color: hasTemplate ? '#52c41a' : '#ff4d4f',
                        fontWeight: "bold" 
                    }}>
                        {hasTemplate ? "✓" : ""}
                    </span>
                )
            },
        },
        {
            title: "Concentrations",
            dataIndex: "concentration",
            align: "center",
            render: (text, record, index) => {
                return <span>{record.concentration + "ng/μl"}</span>
            },
        },

        ...(isAdmin(user.user) ? [
            {
                title: "Price",
                dataIndex: "totalPrice",
                align: "center",
                render: (text, record, index) => {
                    return (
                        <span>
                            {record.totalPrice?.unit === "USD"
                                ? "$" + record.totalPrice.price
                                : (record.totalPrice?.price ?? "") + (record.totalPrice?.unit ?? "")}
                        </span>
                    )
                },
            },
            {
                title: "Paid",
                dataIndex: "paymentInfo",
                align: "center",
                render: (text, record, index) => {
                    const billingType = record.billing_type || "normal"
                    if (billingType === "free rerun" || billingType === "internal") {
                        return <span style={{ fontWeight: 500 }}>--</span>
                    }

                    const isPaid = record.paymentInfo?.paid
                    return (
                        <span style={{ 
                            color: isPaid ? '#52c41a' : '#ff4d4f',
                            fontWeight: 500
                        }}>
                            {isPaid ? 'Yes' : 'No'}
                        </span>
                    )
                },
            },
            
            {
                title: "Invoice",
                dataIndex: "invoice",
                align: "center",
                render: (text, record, index) => {
                    const hasInvoice = record.invoiceId !== null && record.invoiceId !== undefined
                    
                    if (!hasInvoice) {
                        return <span style={{ color: '#bbb' }}>--</span>
                    }

                    const handleInvoiceDownload = async () => {
                        try {
                            // For admin orders, we can directly download the invoice since we don't need user billing info validation
                            const url = `/api/download/invoice/order/${record.id}?r=${Math.random()}`
                            window.open(url, '_blank')
                        } catch (error) {
                            console.error('Error downloading invoice:', error)
                            message.error('Failed to download invoice')
                        }
                    }

                    return (
                        <Tooltip title="Download Invoice">
                            <span
                                style={{
                                    color: '#1890ff',
                                    cursor: 'pointer'
                                }}
                                onClick={handleInvoiceDownload}
                            >
                                #{record.invoiceId}
                            </span>
                        </Tooltip>
                    )
                },
            },
        ] as ColumnsType<OrderInfoInterface> : []),
        {
            title: "Notes",
            dataIndex: "Notes",
            align: "center",
            render: (text, record, index) => {
                const notes = record.specialNotes || ""
                const displayNotes =
                    notes.length > 15 ? notes.slice(0, 15) + "..." : notes || <span style={{ color: "#bbb" }}> </span>
                const isTruncated = notes.length > 15
                return isTruncated ? (
                    <Tooltip title={notes}>
                        <span style={{ cursor: "help" }}>{displayNotes}</span>
                    </Tooltip>
                ) : (
                    <span>{displayNotes}</span>
                )
            },
        },
        {
            title: "nResult",
            dataIndex: "nResults",
            align: "center",
            render: (text, record, index) => {
                const count = record.nResults || 0
                return (
                    <span style={{ 
                        color: count > 0 ? '#52c41a' : '#bbb',
                        fontWeight: count > 0 ? 500 : 'normal'
                    }}>
                        {count}
                    </span>
                )
            },
        },
        {
            title: "Last Comment",
            dataIndex: "comments",
            align: "center",
            render: (text, record, index) => {
                const comments = record.comments ?? []
                if (comments.length === 0) {
                    return <span style={{ color: "#bbb" }}></span>
                }
                const lastComment = comments[comments.length - 1]
                const isUser = lastComment.type === "user"
                const isTruncated = lastComment.content.length > 15
                const commentContent = (
                    <span style={{ display: "flex", alignItems: "center" }}>
                        {isUser && (
                            <span
                                style={{
                                    display: "inline-block",
                                    width: 4,

                                    height: 32,
                                    background: "#faad14",
                                    borderRadius: 2,
                                    marginRight: 6,
                                }}
                            />
                        )}
                        <b style={{ fontSize: "0.68rem" }}>{lastComment.type.slice(0, 1).toUpperCase()}:</b>{" "}
                        <span style={{ fontSize: "0.68rem" }}>
                            {lastComment.content.length > 15
                                ? lastComment.content.slice(0, 15) + ".."
                                : lastComment.content}
                        </span>
                    </span>
                )
                return isTruncated ? (
                    <Tooltip title={lastComment.content}>
                        <span style={{ cursor: "help" }}>{commentContent}</span>
                    </Tooltip>
                ) : (
                    commentContent
                )
            },
        },
        {
            title: "Customer",
            dataIndex: "user",
            align: "center",
            render: (text, record, index) => {
                return (
                    <span>
                        <Link to={`/admin/user/${record.user.id}`}>
                            {record.user.firstName + " " + record.user.lastName}
                        </Link>
                    </span>
                )
            },
        },
        {
            title: "Group",
            dataIndex: "user",
            align: "center",
            filters: getUniqueGroups(),
            onFilter: (value, record) => {
                const groupName = record.user?.group_name
                if (value === "NO_GROUP") {
                    return !groupName
                }
                return groupName === value
            },
            filterSearch: true,
            render: (text, record, index) => {
                const groupName = record.user?.group_name
                return (
                    <span style={{ color: groupName ? "#1890ff" : "#bbb" }}>
                        {groupName || "-"}
                    </span>
                )
            },
        },
        {
            title: "Date created",
            dataIndex: "ts",
            align: "center",
            render: (text, record, index) => {
                return <span>{dayjs(record.ts).format(dateFormat)}</span>
            },
        },

    ]

    // Handler for "Make plate" button
    const handleMakePlate = () => {
        // Send selected order IDs to PlateEditor
        navigate("/admin/editplate", { state: { orderIds: selectedRowKeys } })
    }

    // Handler for "Make invoice" button
    const handleMakeInvoice = () => {
        // Navigate to MakeInvoice page with selected order IDs
        navigate("/admin/makeinvoice", { state: { orderIds: selectedRowKeys } })
    }

    // Handler for "Delete Invoice" button
    const handleDeleteInvoice = () => {
        // Navigate to DeleteInvoice page with selected order IDs
        navigate("/admin/deleteinvoice", { state: { orderIds: selectedRowKeys } })
    }

    // Handler for "Clean Deleted Results" button
    const handleCleanDeletedResults = async () => {
        setCleaningFiles(true)
        try {
            const result = await cleanOrphanedResultFiles()
            if (result.code === 1000) {
                const { totalFiles, orphanedFiles, deletedFiles, errors } = result.details
                if (deletedFiles > 0) {
                    message.success(`Successfully cleaned ${deletedFiles} orphaned files out of ${orphanedFiles} found (${totalFiles} total files scanned)`)
                } else {
                    message.info(`No orphaned files found (${totalFiles} total files scanned)`)
                }
                if (errors && errors.length > 0) {
                    message.warning(`Some files could not be deleted: ${errors.join(', ')}`)
                }
            } else {
                message.error(result.message || 'Failed to clean orphaned files')
            }
        } catch (error) {
            console.error('Error cleaning files:', error)
            message.error('Error occurred while cleaning files')
        } finally {
            setCleaningFiles(false)
        }
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            {contextHolder}
            {modalContextHolder}
            <h2>All Orders</h2>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {/* Refresh Button */}
                <Button onClick={() => queryClient.invalidateQueries({ queryKey: ["getorders", "all", page, pageSize, showCanceledOrders] })} style={{ marginRight: 8 }}>
                    Refresh
                </Button>
               
                {/* Select All Button */}
                <Button 
                    onClick={handleSelectAll}
                    icon={getSelectAllState().allSelected ? <CheckSquareOutlined /> : <BorderOutlined />}
                    style={{ marginRight: 16 }}
                    type={getSelectAllState().someSelected ? 'primary' : 'default'}
                    ghost={getSelectAllState().someSelected && !getSelectAllState().allSelected}
                >
                    {getSelectAllState().allSelected ? 'Deselect All' : 'Select All'} Visible
                </Button>
                
                {/* Action Buttons */}
                <Button disabled={selectedRowKeys.length === 0} onClick={handleMakePlate} style={{ marginRight: 8 }}>
                    Make Plate
                </Button>
               
                {isAdmin(user.user) && (
                    <>
                        <Button disabled={selectedRowKeys.length === 0} onClick={handleMakeInvoice} style={{ marginRight: 8 }}>
                            Make Invoice
                        </Button>
                        <Button 
                            disabled={selectedRowKeys.length === 0} 
                            onClick={handleDeleteInvoice}
                        >
                            Delete Invoice
                        </Button>
                        <Button 
                            loading={cleaningFiles}
                            onClick={handleCleanDeletedResults}
                            style={{ marginLeft: 8 }}
                            type="default"
                        >
                            Clean Deleted Results
                        </Button>
                        <Button 
                            disabled={true}
                            loading={fixAllSampleNumbersMutation.isPending}
                            onClick={() => {
                                modal.confirm({
                                    title: 'Fix All Sample Numbers',
                                    content: 'This will renumber all samples in ALL orders sequentially (1, 2, 3, ...) based on their sample ID order. This operation may take a while. Continue?',
                                    okText: 'Fix All Numbers',
                                    cancelText: 'Cancel',
                                    onOk() {
                                        fixAllSampleNumbersMutation.mutate()
                                    },
                                })
                            }}
                            style={{ marginLeft: 8 }}
                            type="default"
                        >
                            Fix All Sample Numbers
                        </Button>
                    </>
                )}
                {/* Samples by Day Button */}
                <Button onClick={() => setShowSamplesByDay(true)} style={{ marginRight: 8 }}>
                    Orders by Day
                </Button>
                {/* Show Canceled Orders Checkbox */}
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                        Showing {data?.orders?.length || 0} of {data?.total || 0} orders
                    </span>
                    
                    <Checkbox
                        checked={showCanceledOrders}
                        onChange={(e) => { setShowCanceledOrders(e.target.checked); setPage(1) }}
                    >
                        Show canceled orders
                    </Checkbox>
                </div>
            </div>
            {isAdmin(user.user) && (
                <div>
                    <span style={{ color: '#ff4d4f', fontSize: '14px', fontWeight: 'bold' }}>
                            UnInvoiced samples : {getUnInvoicedSamplesCount()}
                        </span>
                </div>
            )}
            <Modal
                title="Order Sample Numbers by Day"
                open={showSamplesByDay}
                onCancel={() => setShowSamplesByDay(false)}
                footer={null}
                width={1100}
            >
                <Table
                    size="small"
                    pagination={false}
                    dataSource={getSamplesByMonth()}
                    defaultExpandAllRows
                    indentSize={20}
                    columns={[
                        {
                            title: 'Date',
                            dataIndex: 'day',
                            width: 140,
                            render: (day: string, record: any) => {
                                const isMonth = !record.children ? false : true
                                return isMonth
                                    ? <strong>{dayjs(day + '-01').format('MMMM YYYY')}</strong>
                                    : <span>{day}</span>
                            },
                        },
                        {
                            title: 'Orders (samples)',
                            dataIndex: 'orders',
                            render: (orderList: { id: number; ordername: string; nSamples: number }[]) => (
                                <span>
                                    {orderList.map((o, i) => (
                                        <span key={o.id}>
                                            {i > 0 && ', '}
                                            <span style={{ whiteSpace: 'nowrap' }}>#{o.id} {o.ordername} ({o.nSamples})</span>
                                        </span>
                                    ))}
                                </span>
                            ),
                        },
                        {
                            title: 'Total Samples',
                            dataIndex: 'total',
                            width: 130,
                            align: 'center',
                            render: (total: number, record: any) => (
                                <span style={{ fontWeight: record.children ? 'bold' : 'normal' }}>{total}</span>
                            ),
                        },
                    ]}
                    scroll={{ y: 720 }}
                />
            </Modal>
            <Table
                ref={tableRef}
                dataSource={(data?.orders || []).map((row) => {
                    return { ...row, key: row.id }
                })}
                columns={columns}
                pagination={{ 
                    position: ["bottomLeft"], 
                    current: page,
                    pageSize: pageSize,
                    total: data?.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['50', '100', '200'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps) },
                }}
                size="middle"
                onRow={(record, index) => {
                    return {
                        onClick: () => {},
                    }
                }}
            />
            {/* <div style={{ marginBottom: "6em" }}>
                <b>Order status colors:</b>
                <Space size="small" style={{ marginLeft: 8 }}>
                    {Object.entries(ORDER_STATUS_COLORS).map(([status, color]) => (
                        <span key={status} style={{ display: "inline-block", textAlign: "center" }}>
                            <div
                                style={{
                                    width: 100,
                                    height: 60,
                                    background: color,
                                    border: "1px solid #ccc",
                                    borderRadius: 4,
                                    display: "inline-block",
                                    marginBottom: 2,
                                }}
                            >
                                {status ?? "Unknown"}
                            </div>
                            <div
                                style={{
                                    width: 100,
                                    height: 60,
                                    fontWeight: "bold",
                                    color: color,
                                    borderRadius: 4,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    marginBottom: 2,
                                    fontSize: 16,
                                    writingMode: "vertical-rl",
                                    textOrientation: "mixed",
                                    border: "1px",
                                    paddingTop: 48,
                                }}
                            >
                                {color ?? "Unknown"}
                            </div>
                        </span>
                    ))}
                </Space>
            </div> */}
        </ContainerFull>
    )
}
